import type { CameraRecord } from '@yawi/types';
import { Camera, Image, VideoOff } from 'lucide-react';
import { CameraPlayer } from './CameraPlayer';

interface CameraCardProps {
  camera: CameraRecord;
  onSelect: (camera: CameraRecord) => void;
  onCapture: (indexCode: string) => void;
  capturing: boolean;
}

const STATUS_LABELS: Record<CameraRecord['status'], string> = {
  online: 'En línea',
  offline: 'Desconectada',
  unknown: 'Desconocido',
};

const STATUS_COLORS: Record<CameraRecord['status'], string> = {
  online: 'bg-green-500',
  offline: 'bg-slate-400',
  unknown: 'bg-yellow-500',
};

export function CameraCard({ camera, onSelect, onCapture, capturing }: CameraCardProps) {
  const isOnline = camera.status === 'online';

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition-shadow">
      {/* Video preview / placeholder — click to open fullscreen */}
      <div
        className={`relative aspect-video bg-slate-900 ${isOnline ? 'cursor-pointer' : ''}`}
        onClick={() => isOnline && onSelect(camera)}
      >
        {isOnline ? (
          <CameraPlayer indexCode={camera.indexCode} compact />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 gap-1">
            <VideoOff className="w-6 h-6" />
            <span className="text-xs">{STATUS_LABELS[camera.status]}</span>
          </div>
        )}

        {/* Click hint overlay for online cameras */}
        {isOnline && (
          <div className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-all">
            <span className="text-white text-xs font-medium bg-black/60 px-3 py-1 rounded-full">
              Click para ampliar
            </span>
          </div>
        )}
      </div>

      {/* Info bar */}
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[camera.status]}`} />
            <Camera className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="font-medium text-slate-800 text-sm truncate">{camera.name}</span>
          </div>
          <button
            onClick={() => onCapture(camera.indexCode)}
            disabled={!isOnline || capturing}
            className="flex items-center gap-1 text-xs py-1 px-2 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            <Image className="w-3 h-3" />
            {capturing ? '...' : 'Foto'}
          </button>
        </div>
        {camera.deviceIp && (
          <p className="text-[11px] text-slate-400 font-mono">{camera.deviceIp}</p>
        )}
      </div>
    </div>
  );
}
